'use client';

import React, { useEffect, useState, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Head from 'next/head';
import Image from 'next/image';
import { validateDevice, validateCompanyCode } from '@/lib/actions/general.action';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faCheckCircle, faClock, faRegistered } from '@fortawesome/free-solid-svg-icons';
import { BKGROUND_IMAGE_BOOKSHELF } from '@/constants/general';
import Footer from '@/components/layouts/footer';
import { IRootState } from '@/store';
import { useSelector } from 'react-redux';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import useOrientation from '@/utils/useOrientation';
import { showToast } from '@/constants/sweetalerts';
import { showAlert } from '@/constants/messagebox';

const BSLandingPage = () => {
    const router = useRouter();

    const [deviceStatus, setDeviceStatus] = useState<{ message: string; code: string; companyId?: number; manualName?: string } | null>(null);
    const [showCompanyRegistration, setShowCompanyRegistration] = useState(false);
    const [companyCode, setCompanyCode] = useState('');
    const [isValidatingCode, setIsValidatingCode] = useState(false);

    const companyRegistrationDialogRef = useRef<DialogComponent>(null);
    const { isPortrait, deviceType } = useOrientation();
    const [animation] = useState<object>({ effect: 'Fade', duration: 400 });

    const searchParams = useSearchParams(); // Add this line
    const uuid = searchParams.get('uuid');
    const code = searchParams.get('code');
    const deviceName = searchParams.get('deviceName');

    const isDark = useSelector((state: IRootState) => state.themeConfig.theme === 'dark' || state.themeConfig.isDarkMode);

    const urlcode = uuid && deviceName && code ? `?code=${code}&uuid=${uuid}&deviceName=${deviceName}` : '';

    // Function to handle company registration
    const handleCompanyRegistration = async () => {
        if (!companyCode || companyCode.length !== 5) {
            showAlert({
                title: `Company Registration Issue!`,
                message: 'Please enter a valid 5-character company code.',
                type: 'error',
                timerDuration: 0,
                target: companyRegistrationDialogRef.current?.element,
            });

            return;
        }

        setIsValidatingCode(true);
        try {
            const result = await validateCompanyCode(companyCode.toUpperCase());

            if (result.code === 'SUCCESS') {
                await showToast({
                    title: `Company validated successfully!`,
                    position: 'center',
                    type: 'success',
                    // target: companyRegistrationDialogRef.current?.element,
                });

                // Close the dialog
                setShowCompanyRegistration(false);
                setCompanyCode('');

                window.postMessage(
                    {
                        type: 'CompanyCodeValidated',
                        reason: 'Company code validated',
                        companyId: result.companyId,
                    },
                    '*',
                );

                if (window.gc) {
                    window.gc();
                }

                // Reload the page after a short delay
                // setTimeout(() => {
                //     window.location.reload();
                // }, 2000);
            } else {
                showAlert({
                    title: `Company Registration Issue!`,
                    message: result.message,
                    type: 'error',
                    timerDuration: 0,
                    target: companyRegistrationDialogRef.current?.element,
                });

                return false;
            }
        } catch (error) {
            console.error('Error validating company code:', error);
            showAlert({
                title: `Company Registration Issue!`,
                message: 'Error validating company code. Please try again.',
                type: 'error',
                timerDuration: 0,
                target: companyRegistrationDialogRef.current?.element,
            });
        } finally {
            setIsValidatingCode(false);
        }
    };

    // Function to handle company code input (uppercase only)
    const handleCompanyCodeInput = (value: string) => {
        const upperCaseValue = value.toUpperCase().slice(0, 5); // Limit to 5 characters
        setCompanyCode(upperCaseValue);
    };

    useEffect(() => {
        // Debug logging
        // console.log('Debug Info:', {
        //     uuid: uuid,
        //     code: code,
        //     deviceName: deviceName,
        //     showCompanyRegistration: showCompanyRegistration,
        // });

        if (code && uuid) {
            validateDevice({
                uuid,
                code,
                deviceName,
            })
                .then((res) => {
                    if (res.code === 'success') {
                        setDeviceStatus({ message: res.message, code: res.code, companyId: res.companyId, manualName: res.manualName });
                    } else {
                        setDeviceStatus({ message: res.message, code: res.code });
                    }

                    // console.log('Device validation result:', res);
                })
                .catch((err) => {
                    console.error('Error validating device:', err);
                    setDeviceStatus({ message: 'Error validating device', code: 'error' });
                });
        }
    }, [uuid, code, deviceName, showCompanyRegistration]);

    return (
        <section
            className="relative flex min-h-screen w-full flex-col overflow-auto"
            style={{
                WebkitOverflowScrolling: 'touch', // iOS momentum scrolling
                touchAction: 'pan-y', // Enable vertical scrolling
                backgroundImage: `url(${BKGROUND_IMAGE_BOOKSHELF})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
            }}
        >
            <Head>
                <title>BookShelf | Enterprise Solution</title>
                <meta name="description" content="Powerful enterprise solution for book sellers" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
                <link rel="icon" href="/assets/images/bookshelf.svg" />
            </Head>

            <section
                className="mx-2 flex w-full items-center justify-between bg-black/10 backdrop-blur-md"
                style={{
                    borderBottomLeftRadius: 16,
                    borderBottomRightRadius: 16,
                    alignSelf: 'center',
                    width: '100%',
                    height: '100px',
                }}
            >
                <div className="ml-6 flex h-full items-center md:ml-12">
                    <Image className="inline w-8" src={`/assets/images/bookshelf_dark.svg`} alt="logo" width={80} height={20} />
                    <span className="ml-2 align-middle text-2xl font-semibold text-white-light transition-all duration-300">BookShelf</span>
                </div>

                {/* Device Status Display */}
                {deviceStatus && uuid && code && (
                    <div className="fixed right-4 top-4 z-50 flex flex-col items-center">
                        {deviceStatus.code === 'REGISTERED' || deviceStatus.code === 'PENDING APPROVAL' ? (
                            <div className="flex cursor-not-allowed items-center justify-center rounded-lg bg-yellow-300 px-4 py-1 text-black">
                                <FontAwesomeIcon icon={faClock} className="mr-2 size-4" />
                                Approval Pending
                            </div>
                        ) : deviceStatus.code === 'BLACKLISTED' ? (
                            <div className="flex cursor-not-allowed items-center justify-center rounded-lg bg-red-500 px-4 py-1 text-white">
                                <FontAwesomeIcon icon={faBan} className="mr-2 size-4 text-white" />
                                Device Removed
                            </div>
                        ) : deviceStatus.code === 'success' ? (
                            <div className="flex flex-col items-center space-y-1">
                                <button
                                    className="w-28 rounded-lg bg-white px-4 py-2 font-medium text-blue-600 shadow-sm transition hover:bg-blue-100"
                                    onClick={() => router.push(`/sign-in/${urlcode}`)}
                                >
                                    Sign In
                                </button>
                                <div className="flex w-28 cursor-not-allowed items-center justify-center rounded-lg bg-green-500 px-4 py-1 font-medium text-white shadow-sm hover:bg-green-600">
                                    <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-white" />
                                    <span className="truncate">
                                        {!deviceStatus.manualName && 'xxxxx' + (uuid ? uuid.slice(-5) : 'XXXXX')}
                                        {deviceStatus.manualName && deviceStatus.manualName}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="flex cursor-not-allowed items-center justify-center rounded-lg bg-red-500 px-4 py-2 text-white">
                                <FontAwesomeIcon icon={faBan} className="mr-2 size-4 text-white" />
                                {deviceStatus.message}
                            </div>
                        )}

                        {deviceStatus.manualName && deviceStatus.code !== 'success' && (
                            <div className="mt-1 w-28 cursor-not-allowed text-center text-xs font-bold italic text-white">{deviceStatus.manualName}</div>
                        )}
                        {!deviceStatus.manualName && deviceStatus.code !== 'success' && (
                            <div className="mt-1 w-28 cursor-not-allowed text-center text-xs font-bold italic text-white">
                                {(deviceName ? deviceName.slice(0, 10) : 'Unknown Device') + '-' + (uuid ? uuid.slice(-5) : 'XXXXX')}
                            </div>
                        )}
                    </div>
                )}

                {/* Register Company button in top-left corner */}
                {uuid && !code && !showCompanyRegistration && (
                    <div className="fixed right-4 top-4 z-50 flex flex-col items-center">
                        <button
                            onClick={() => setShowCompanyRegistration(true)}
                            className="flex w-28 items-center justify-center rounded-lg bg-blue-600 px-4 py-2 font-medium text-white shadow-sm transition hover:bg-blue-700"
                        >
                            <FontAwesomeIcon icon={faRegistered} className="mr-2 size-5 text-white" />
                            Register Company
                        </button>
                    </div>
                )}

                {/* Company Registration Dialog */}
                {showCompanyRegistration && (
                    <DialogComponent
                        id="companyRegistration"
                        header="Register Company"
                        visible={true}
                        showCloseIcon={true}
                        width={isPortrait || ['tablet', 'mobile', 'ipadpro'].includes(deviceType) ? '90%' : '400px'}
                        height={'auto'}
                        isModal={true}
                        allowDragging={true}
                        closeOnEscape={true}
                        overlayClick={() => {
                            setShowCompanyRegistration(false);
                            setCompanyCode('');
                            setIsValidatingCode(false);
                        }}
                        close={() => {
                            setShowCompanyRegistration(false);
                            setCompanyCode('');
                            setIsValidatingCode(false);
                        }}
                        animationSettings={animation}
                        enableResize={false}
                        zIndex={10002}
                        ref={companyRegistrationDialogRef}
                    >
                        <div className="p-4">
                            <div className="mb-6 text-center">
                                <p className="text-sm text-gray-600 sm:text-base">Enter your 5-character company code</p>
                            </div>

                            <div className="mb-6">
                                <input
                                    type="text"
                                    value={companyCode}
                                    onChange={(e) => handleCompanyCodeInput(e.target.value)}
                                    placeholder="XXXXX"
                                    className="w-full rounded-lg border border-gray-300 px-4 py-3 text-center font-mono text-lg uppercase tracking-widest focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                                    maxLength={5}
                                    disabled={isValidatingCode}
                                    autoFocus
                                />
                            </div>

                            <div className="flex flex-col space-y-3 sm:flex-row sm:space-x-3 sm:space-y-0">
                                <button
                                    onClick={() => {
                                        setShowCompanyRegistration(false);
                                        setCompanyCode('');
                                        setIsValidatingCode(false);
                                    }}
                                    className="flex-1 rounded-lg bg-gray-200 py-2 text-gray-700 transition hover:bg-gray-300 sm:py-3"
                                    disabled={isValidatingCode}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleCompanyRegistration}
                                    className="flex-1 rounded-lg bg-blue-600 py-2 text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 sm:py-3"
                                    disabled={isValidatingCode || companyCode.length !== 5}
                                >
                                    {isValidatingCode ? (
                                        <div className="flex items-center justify-center">
                                            <div className="mr-2 size-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                                            <span className="text-sm sm:text-base">Validating...</span>
                                        </div>
                                    ) : (
                                        <span className="text-sm sm:text-base">Register</span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </DialogComponent>
                )}
            </section>

            {/* Main Content */}
            <div className="flex flex-1 flex-col">
                {/* Hero Section */}
                <section
                    id="hero"
                    className="relative mx-auto mt-24 flex flex-col items-start justify-center bg-black/50 px-4 py-6 text-white md:ml-12 md:mr-auto md:mt-10 md:py-10 md:pl-12 md:pr-4"
                    style={{
                        borderRadius: 16,
                        alignSelf: 'center',
                        width: '90%',
                        maxWidth: 600,
                    }}
                >
                    <div className="w-full max-w-[600px]">
                        <h1 className="mb-4 text-left text-4xl font-bold leading-tight md:text-5xl">Transform Your Business with Our Platform</h1>
                        <p className="mb-4 text-left text-xl text-blue-100">The all-in-one solution trusted by enterprises worldwide to streamline operations and drive growth.</p>
                    </div>
                </section>

                {/* CTA Section */}
                <section
                    className="relative mx-auto my-8 flex flex-col items-end justify-center bg-black/50 px-4 py-6 text-white md:my-0 md:mr-12 md:py-10 md:pl-4 md:pr-12"
                    style={{
                        borderRadius: 16,
                        alignSelf: 'flex-end',
                        width: '90%',
                        maxWidth: 500,
                        marginTop: 'auto',
                    }}
                >
                    <div className="w-full max-w-[500px] text-right">
                        <h2 className="mb-4 text-3xl font-bold md:text-4xl">Ready to Transform Your Business?</h2>
                        <p className="mx-auto mb-4 max-w-3xl text-xl text-blue-100">Join thousands of enterprises that trust our platform to power their operations.</p>
                    </div>
                </section>
            </div>

            {/* Footer at the bottom */}
            <div className="mt-auto">
                <Footer textDark={false} bookStoreRazorPayMode={false} />
            </div>
        </section>
    );
};

export default React.memo(BSLandingPage);
